import {
  Component,
  ChangeDetectionStrategy,
  OnInit,
  computed,
  inject,
  signal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule, CurrencyPipe, DatePipe } from '@angular/common';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { TableModule, type TableLazyLoadEvent } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { ButtonModule } from 'primeng/button';
import { DrawerModule } from 'primeng/drawer';
import { DialogModule } from 'primeng/dialog';
import { SkeletonModule } from 'primeng/skeleton';
import { FloatLabelModule } from 'primeng/floatlabel';
import { SelectModule } from 'primeng/select';
import { MultiSelectModule } from 'primeng/multiselect';
import { DatePickerModule } from 'primeng/datepicker';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { ToastrService } from 'ngx-toastr';
import { debounceTime, distinctUntilChanged, merge, of } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { httpErrorApiMessage } from '../../core/api-response-utils';
import {
  ChargeOperationalStatusEnum,
  CollectionMethodEnum,
  FrequencyEnum,
  SaleTypeEnum,
  type ChargeDTO,
  type ChargeOperationalStatusEnumValue,
  type CollectionMethodEnumValue,
  type FrequencyEnumValue,
  type SaleTypeEnumValue
} from '../../core/models';
import type { SearchChargeFilterSpec } from '../../services/service-common';
import { AuthService } from '../../services/auth.service';
import { BoardService } from '../../services/board.service';
import { ChargeService } from '../../services/charge.service';
import { CustomerService, type CustomerDTO } from '../../services/customer.service';
import { SaleService, type SaleDTO } from '../../services/sale.service';
import { ChargeReadComponent } from '../charges/charge-read.component';
import { CustomerReadComponent } from '../customers/customer-read.component';
import { ChargeFollowupDialogComponent } from '../sales/charge-followup-dialog.component';
import { SaleReadComponent } from '../sales/sale-read.component';
import type { BoardConfigDocument, BoardScope } from '../../core/models';
import { calendarDaysUntil, formatApiDateOnly } from '../../core/date-utils';

type CollectionBucketFilter = 'ALL' | 'OVERDUE' | 'UPCOMING';
type CollectionsViewMode = 'table' | 'board';
/** Opción limpia: sin campos extra del API (p.ej. `value`) que rompen el Autocomplete de PrimeNG. */
type CustomerOption = {
  id: number;
  comercialName?: string;
  legalName?: string;
  displayLabel: string;
};

/** Filtros de un panel del tablero de cobranza (BoardConfig V1). */
interface CollectionsBoardPanelCustomer {
  id: number;
  label: string;
}

interface CollectionsBoardPanelFilters {
  customers: CollectionsBoardPanelCustomer[];
  collectionMethods: CollectionMethodEnumValue[];
  statuses: ChargeOperationalStatusEnumValue[];
  overdueDaysFrom: number | null;
  overdueDaysTo: number | null;
  dueInDaysFrom: number | null;
  dueInDaysTo: number | null;
  attemptCountMin: number | null;
  cxcEmail: boolean | null;
  cxcEmailOpened: boolean | null;
  cxcInternal: boolean | null;
  lastSentEmailDaysFrom: number | null;
  lastSentEmailDaysTo: number | null;
  lastSentInternalDaysFrom: number | null;
  lastSentInternalDaysTo: number | null;
  saleFrequencies: FrequencyEnumValue[];
  saleStatuses: string[];
  saleTypes: SaleTypeEnumValue[];
}

interface CollectionsBoardPanel {
  id: number;
  title: string;
  scope: BoardScope;
  sortOrder: number;
  filters: CollectionsBoardPanelFilters;
}

interface BoardPanelResult {
  loading: boolean;
  error: string | null;
  totalItems: number;
}

const PANEL_DRAWER_PAGE_SIZE = 20;

@Component({
  selector: 'app-collections',
  templateUrl: './collections.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    CurrencyPipe,
    DatePipe,
    TableModule,
    TooltipModule,
    ButtonModule,
    DrawerModule,
    DialogModule,
    SkeletonModule,
    FloatLabelModule,
    SelectModule,
    MultiSelectModule,
    DatePickerModule,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    InputNumberModule,
    AutoCompleteModule,
    ChargeReadComponent,
    CustomerReadComponent,
    SaleReadComponent,
    ChargeFollowupDialogComponent
  ],
  styles: [
    `
      :host {
        display: flex;
        flex-direction: column;
        flex: 1 1 0%;
        min-height: 0;
        width: 100%;
      }
      :host ::ng-deep .collections-primeng-table .p-datatable-thead > tr > th {
        background: rgb(248 250 252);
        border-color: rgb(226 232 240);
        color: rgb(100 116 139);
        font-size: 0.75rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        padding: 0.55rem 0.5rem !important;
        position: sticky;
        top: 0;
        z-index: 20;
        transition: background-color 0.15s ease, color 0.15s ease;
      }
      :host ::ng-deep .collections-primeng-table .p-datatable-thead > tr > th.p-sortable-column {
        cursor: pointer;
        user-select: none;
      }
      :host ::ng-deep .collections-primeng-table .p-datatable-thead > tr > th.p-sortable-column:hover {
        background: rgb(241 245 249);
        color: rgb(15 23 42);
      }
      :host ::ng-deep .collections-primeng-table .p-datatable-thead > tr > th.p-sortable-column:focus-visible {
        outline: 2px solid rgb(14 165 233);
        outline-offset: -2px;
      }
      :host ::ng-deep .collections-primeng-table .p-datatable-thead > tr > th.p-sortable-column.p-datatable-column-sorted {
        background: rgb(240 249 255);
        color: rgb(3 105 161);
      }
      :host ::ng-deep .collections-primeng-table .p-datatable-thead > tr > th.p-sortable-column .p-datatable-sort-icon,
      :host ::ng-deep .collections-primeng-table .p-datatable-thead > tr > th.p-sortable-column .p-sortable-column-icon,
      :host ::ng-deep .collections-primeng-table .p-datatable-thead > tr > th.p-sortable-column p-sorticon svg {
        width: 0.75rem;
        height: 0.75rem;
        color: rgb(14 165 233);
        opacity: 1;
        flex-shrink: 0;
        margin-left: auto;
      }
      :host ::ng-deep .collections-primeng-table .p-datatable-tbody > tr > td {
        padding: 0.4rem 0.45rem !important;
        overflow: visible;
      }
      :host ::ng-deep .collections-primeng-table .p-datatable-tbody > tr > td:first-child {
        overflow: visible;
      }
      .cxc-status-badge {
        display: inline-grid;
        place-items: center;
        line-height: 0;
      }
      .cxc-status-badge i.pi {
        display: block;
        margin: 0;
        padding: 0;
        line-height: 1;
        width: 1em;
        height: 1em;
      }
      .cxc-status-badge i.pi::before {
        display: block;
        line-height: 1;
      }
      :host ::ng-deep .collections-primeng-table .p-datatable-table {
        width: 100%;
        table-layout: auto;
      }
      :host ::ng-deep p-table {
        display: flex;
        flex-direction: column;
        height: 100%;
        min-height: 0;
      }
      :host ::ng-deep .collections-primeng-table.p-datatable,
      :host ::ng-deep .p-datatable {
        display: flex;
        flex-direction: column;
        height: 100%;
        min-height: 0;
      }
      :host ::ng-deep .p-datatable-table-container,
      :host ::ng-deep .p-datatable-flex-scrollable > .p-datatable-table-container {
        flex: 1 1 0;
        min-height: 0;
        overflow: auto;
      }
      :host ::ng-deep .p-datatable-paginator-bottom {
        flex-shrink: 0;
      }
      :host ::ng-deep .p-paginator .p-paginator-page.p-paginator-page-selected,
      :host ::ng-deep button.p-paginator-page-selected {
        background: rgb(224 242 254) !important;
        border-color: rgb(14 165 233) !important;
        color: rgb(2 132 199) !important;
      }
      :host ::ng-deep .p-autocomplete {
        width: 100%;
      }
      :host ::ng-deep .p-autocomplete .p-autocomplete-input {
        width: 100%;
      }
      :host ::ng-deep .p-multiselect {
        width: 100%;
      }
      :host ::ng-deep .p-inputnumber {
        width: 100%;
      }
      :host ::ng-deep .p-inputnumber input {
        width: 100%;
      }
      :host ::ng-deep .sale-details-drawer .p-drawer-header {
        background: rgb(241 245 249);
        border-bottom: 1px solid rgb(226 232 240);
        box-shadow: 0 1px 3px 0 rgb(15 23 42 / 0.06);
        padding: 1rem 1.25rem;
      }
      :host ::ng-deep .sale-details-drawer .p-drawer-content {
        background: rgb(248 250 252);
        padding: 1rem 1.25rem;
      }
      :host ::ng-deep .customer-details-drawer .p-drawer-header {
        background: rgb(241 245 249);
        border-bottom: 1px solid rgb(226 232 240);
        box-shadow: 0 1px 3px 0 rgb(15 23 42 / 0.06);
        padding: 1rem 1.25rem;
      }
      :host ::ng-deep .customer-details-drawer .p-drawer-content {
        background: rgb(248 250 252);
        padding: 1rem 1.25rem;
      }
      :host ::ng-deep .collections-panel-dialog.p-dialog {
        max-width: 95vw;
        overflow: hidden;
      }
      :host ::ng-deep .collections-panel-dialog .p-dialog-content {
        overflow-x: hidden;
        overflow-y: auto;
        max-height: min(70vh, 36rem);
      }
      .panel-compose-row {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        min-height: 2.125rem;
        padding: 0.25rem 0.75rem;
        border-bottom: 1px solid rgb(241 245 249);
      }
      .panel-compose-row--last {
        border-bottom: none;
      }
      .panel-compose-label {
        flex-shrink: 0;
        width: 5.25rem;
        font-size: 0.75rem;
        font-weight: 500;
        color: rgb(100 116 139);
        user-select: none;
      }
      .panel-compose-section {
        display: flex;
        align-items: center;
        padding: 0.4rem 0.75rem 0.2rem;
        background: rgb(248 250 252);
        border-bottom: 1px solid rgb(241 245 249);
      }
      .panel-compose-section-label {
        font-size: 0.625rem;
        font-weight: 600;
        letter-spacing: 0.06em;
        text-transform: uppercase;
        color: rgb(100 116 139);
      }
      .panel-compose-section-label--sale {
        color: rgb(124 58 237);
      }
      .panel-compose-field {
        display: flex;
        flex-direction: column;
        gap: 0.125rem;
        min-width: 0;
      }
      .panel-compose-range {
        flex-direction: row;
        align-items: center;
        gap: 0.4rem;
        flex-wrap: wrap;
      }
      .panel-compose-range-sep,
      .panel-compose-range-unit {
        flex-shrink: 0;
        font-size: 0.75rem;
        color: rgb(148 163 184);
      }
      :host ::ng-deep .collections-panel-dialog .panel-compose-input,
      :host ::ng-deep .collections-panel-dialog .panel-compose-input.p-inputtext {
        border: none !important;
        box-shadow: none !important;
        background: transparent !important;
        padding: 0.125rem 0 !important;
        font-size: 0.875rem;
        line-height: 1.4;
        color: rgb(15 23 42);
        border-radius: 0;
        min-height: 0;
        height: auto;
      }
      :host ::ng-deep .collections-panel-dialog .panel-compose-input::placeholder {
        color: rgb(203 213 225);
      }
      :host ::ng-deep .collections-panel-dialog .panel-compose-input:focus {
        outline: none;
        box-shadow: none !important;
      }
      :host ::ng-deep .collections-panel-dialog .panel-compose-multiselect.p-multiselect {
        border: none !important;
        box-shadow: none !important;
        background: transparent !important;
        min-height: 0;
        width: 100%;
        max-width: 100%;
      }
      :host ::ng-deep .collections-panel-dialog .panel-compose-select.p-select {
        border: none !important;
        box-shadow: none !important;
        background: transparent !important;
        width: 100%;
      }
      :host ::ng-deep .collections-panel-dialog .panel-compose-select .p-select-label {
        padding: 0.125rem 0 !important;
        font-size: 0.875rem;
        color: rgb(15 23 42);
      }
      :host ::ng-deep .collections-panel-dialog .panel-compose-select .p-select-label.p-placeholder {
        color: rgb(148 163 184) !important;
        font-size: 0.75rem;
        font-weight: 500;
        opacity: 1 !important;
      }
      :host ::ng-deep .collections-panel-dialog .panel-compose-multiselect .p-multiselect-label {
        padding: 0.125rem 0 !important;
        font-size: 0.875rem;
        color: rgb(15 23 42);
      }
      :host ::ng-deep .collections-panel-dialog .panel-compose-multiselect .p-multiselect-label.p-placeholder {
        color: rgb(203 213 225);
      }
      :host ::ng-deep .collections-panel-dialog .panel-compose-multiselect .p-multiselect-dropdown {
        width: 1.5rem;
        color: rgb(148 163 184);
      }
      :host ::ng-deep .collections-panel-dialog .panel-compose-multiselect .p-multiselect-chip,
      :host ::ng-deep .collections-panel-dialog .panel-compose-autocomplete .p-autocomplete-chip,
      :host ::ng-deep .collections-panel-dialog .panel-compose-autocomplete .p-chip {
        font-size: 0.7rem;
        height: 1.375rem;
        padding-block: 0.1rem;
        background: rgb(241 245 249);
        border: none;
        color: rgb(51 65 85);
      }
      :host ::ng-deep .collections-panel-dialog .panel-compose-autocomplete.p-autocomplete,
      :host ::ng-deep .collections-panel-dialog .panel-compose-autocomplete.p-autocomplete-fluid {
        width: 100%;
        min-width: 0;
        display: flex;
      }
      :host ::ng-deep .collections-panel-dialog .panel-compose-autocomplete .p-autocomplete-input-multiple {
        width: 100%;
        min-width: 0;
        min-height: 0;
        height: auto;
        padding: 0 !important;
        border: none !important;
        box-shadow: none !important;
        background: transparent !important;
        flex-wrap: wrap;
        align-items: center;
        gap: 0.25rem;
      }
      :host ::ng-deep .collections-panel-dialog .panel-compose-autocomplete .p-autocomplete-input-chip input {
        padding: 0.125rem 0 !important;
        font-size: 0.875rem;
      }
      :host ::ng-deep .collections-panel-dialog .panel-compose-days.p-inputnumber {
        width: 3.25rem !important;
        max-width: 3.25rem !important;
        display: inline-flex;
        flex-shrink: 0;
      }
      :host ::ng-deep .collections-panel-dialog .panel-compose-days .p-inputnumber-input {
        min-height: 0;
        height: auto;
        text-align: center;
      }
      :host ::ng-deep .collections-panel-dialog .p-dialog-footer {
        background: rgb(248 250 252);
        border-top: 1px solid rgb(226 232 240);
        padding: 0.875rem 1.25rem;
      }
      :host ::ng-deep .panel-charges-drawer .p-drawer-header {
        background: rgb(241 245 249);
        border-bottom: 1px solid rgb(226 232 240);
        box-shadow: 0 1px 3px 0 rgb(15 23 42 / 0.06);
        padding: 1rem 1.25rem;
      }
      :host ::ng-deep .panel-charges-drawer .p-drawer-content {
        background: rgb(248 250 252);
        padding: 0;
        display: flex;
        flex-direction: column;
        min-height: 0;
      }
      :host ::ng-deep .panel-charges-drawer .collections-primeng-table .p-datatable-thead > tr > th {
        position: sticky;
        top: 0;
        z-index: 20;
      }
    `
  ]
})
export class CollectionsComponent implements OnInit {
  private chargeService = inject(ChargeService);
  private boardService = inject(BoardService);
  private saleService = inject(SaleService);
  private customerService = inject(CustomerService);
  private authService = inject(AuthService);
  private toastr = inject(ToastrService);
  private fb = inject(FormBuilder);

  readonly canExportCharges = this.authService.hasRole('CHARGE_EXPORT');
  exporting = signal(false);

  get exportButtonTooltip(): string {
    if (!this.canExportCharges) {
      return 'No tienes permiso para descargar este listado. Pide acceso a un administrador.';
    }
    if (this.totalItems() === 0) {
      return 'No hay recibos para exportar con los filtros actuales';
    }
    return 'Descargar todos los recibos filtrados en CSV';
  }

  viewMode = signal<CollectionsViewMode>('table');

  charges = signal<ChargeDTO[]>([]);
  loading = signal(false);
  initialLoadDone = signal(false);
  error = signal<string | null>(null);
  hasActiveFilters = signal(false);

  currentPage = signal(0);
  pageSize = signal(15);
  totalItems = signal(0);
  pageSizeOptions = [15, 30, 50];
  sortField = signal('dueDate');
  sortOrder = signal(1);

  selectedCharge = signal<ChargeDTO | null>(null);
  isDetailsOpen = signal(false);
  chargeDetailsLoading = signal(false);
  seguimientoDialogVisible = signal(false);
  seguimientoDialogChargeId = signal<number | null>(null);
  seguimientoDialogSaleId = signal<number | null>(null);
  seguimientoDialogSubtitle = signal<string | null>(null);
  selectedSale = signal<SaleDTO | null>(null);
  saleCharges = signal<ChargeDTO[]>([]);
  isSaleDetailsOpen = signal(false);
  saleDetailsLoading = signal(false);
  selectedCustomer = signal<CustomerDTO | null>(null);
  isCustomerDetailsOpen = signal(false);
  customerDetailsLoading = signal(false);

  boardPanels = signal<CollectionsBoardPanel[]>([]);
  boardResults = signal<Record<string, BoardPanelResult>>({});
  boardsLoading = signal(false);
  panelDialogVisible = signal(false);
  editingPanelId = signal<number | null>(null);
  panelCustomerOptions = signal<CustomerOption[]>([]);
  panelCustomerLoading = signal(false);
  panelSaving = signal(false);

  selectedBoardPanel = signal<CollectionsBoardPanel | null>(null);
  isPanelChargesDrawerOpen = signal(false);
  panelDrawerCharges = signal<ChargeDTO[]>([]);
  panelDrawerLoading = signal(false);
  panelDrawerError = signal<string | null>(null);
  panelDrawerPage = signal(0);
  panelDrawerPageSize = signal(PANEL_DRAWER_PAGE_SIZE);
  panelDrawerTotalItems = signal(0);

  readonly skeletonRows = [1, 2, 3, 4, 5, 6, 7, 8];
  readonly asOfLabel = computed(() => {
    const d = new Date();
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
  });
  readonly boardRefreshing = computed(() =>
    Object.values(this.boardResults()).some((r) => r.loading)
  );

  private readonly pendingStatuses: ChargeOperationalStatusEnumValue[] = [
    ChargeOperationalStatusEnum.SCHEDULED,
    ChargeOperationalStatusEnum.PENDING,
    ChargeOperationalStatusEnum.DUE,
    ChargeOperationalStatusEnum.FAILED
  ];

  readonly bucketFilterOptions: { label: string; value: CollectionBucketFilter }[] = [
    { label: 'Todos', value: 'ALL' },
    { label: 'Por vencer', value: 'UPCOMING' },
    { label: 'Vencidos', value: 'OVERDUE' }
  ];

  readonly yesNoFilterOptions: { label: string; value: boolean }[] = [
    { label: 'Sí', value: true },
    { label: 'No', value: false }
  ];

  readonly collectionMethodOptions = [
    { value: CollectionMethodEnum.LINK, label: 'Liga de pago' },
    { value: CollectionMethodEnum.MANUAL, label: 'Manual' },
    { value: CollectionMethodEnum.RECURRING_CHARGE, label: 'Cargo recurrente' },
    { value: CollectionMethodEnum.MERCHANT_INITIATED, label: 'Cobro bajo demanda' }
  ];

  readonly statusFilterOptions: { value: ChargeOperationalStatusEnumValue; label: string }[] = [
    { value: ChargeOperationalStatusEnum.SCHEDULED, label: 'Programado' },
    { value: ChargeOperationalStatusEnum.PENDING, label: 'Pendiente' },
    { value: ChargeOperationalStatusEnum.DUE, label: 'Vencido' },
    { value: ChargeOperationalStatusEnum.FAILED, label: 'Fallido' },
    { value: ChargeOperationalStatusEnum.PAID, label: 'Pagado' },
    { value: ChargeOperationalStatusEnum.EXPIRED, label: 'Expirado' },
    { value: ChargeOperationalStatusEnum.CANCELLED, label: 'Cancelado' }
  ];

  readonly saleFrequencyOptions: { value: FrequencyEnumValue; label: string }[] = [
    { value: FrequencyEnum.ONCE, label: 'Una sola vez' },
    { value: FrequencyEnum.DAILY, label: 'Diario' },
    { value: FrequencyEnum.WEEKLY, label: 'Semanal' },
    { value: FrequencyEnum.BIWEEKLY, label: 'Quincenal' },
    { value: FrequencyEnum.MONTHLY, label: 'Mensual' },
    { value: FrequencyEnum.QUARTERLY, label: 'Trimestral' },
    { value: FrequencyEnum.SEMIANNUALLY, label: 'Semestral' },
    { value: FrequencyEnum.ANNUALLY, label: 'Anual' }
  ];

  readonly saleStatusOptions: { value: string; label: string }[] = [
    { value: 'DRAFT', label: 'Borrador' },
    { value: 'ISSUED', label: 'Emitida' },
    { value: 'PARTIALLY_PAID', label: 'Parcialmente pagada' },
    { value: 'PAID', label: 'Pagada' },
    { value: 'CANCELLED', label: 'Cancelada' },
    { value: 'UNCOLLECTIBLE', label: 'Incobrable' }
  ];

  readonly saleTypeOptions: { value: SaleTypeEnumValue; label: string }[] = [
    { value: SaleTypeEnum.ONE_TIME, label: 'Venta única' },
    { value: SaleTypeEnum.SUBSCRIPTION, label: 'Suscripción' }
  ];

  readonly scopeOptions: { value: BoardScope; label: string }[] = [
    { value: 'PERSONAL', label: 'Personal (solo yo)' },
    { value: 'GLOBAL', label: 'Global (toda la compañía)' }
  ];

  filtersForm = this.fb.group({
    saleNumber: [''],
    customerName: [''],
    collectionMethod: this.fb.control<CollectionMethodEnumValue | null>(null),
    dueDateRange: this.fb.control<Date[] | null>(null),
    bucket: this.fb.control<CollectionBucketFilter>('ALL')
  });

  panelForm = this.fb.group({
    title: [''],
    scope: this.fb.control<BoardScope>('PERSONAL'),
    customerSelection: this.fb.control<CustomerOption[]>([]),
    collectionMethods: this.fb.control<CollectionMethodEnumValue[]>([]),
    statuses: this.fb.control<ChargeOperationalStatusEnumValue[]>([...this.pendingStatuses]),
    overdueDaysFrom: this.fb.control<number | null>(null),
    overdueDaysTo: this.fb.control<number | null>(null),
    dueInDaysFrom: this.fb.control<number | null>(null),
    dueInDaysTo: this.fb.control<number | null>(null),
    attemptCountMin: this.fb.control<number | null>(null),
    cxcEmail: this.fb.control<boolean | null>(null),
    cxcEmailOpened: this.fb.control<boolean | null>(null),
    cxcInternal: this.fb.control<boolean | null>(null),
    lastSentEmailDaysFrom: this.fb.control<number | null>(null),
    lastSentEmailDaysTo: this.fb.control<number | null>(null),
    lastSentInternalDaysFrom: this.fb.control<number | null>(null),
    lastSentInternalDaysTo: this.fb.control<number | null>(null),
    saleFrequencies: this.fb.control<FrequencyEnumValue[]>([]),
    saleStatuses: this.fb.control<string[]>([]),
    saleTypes: this.fb.control<SaleTypeEnumValue[]>([])
  });

  constructor() {
    this.filtersForm.controls.saleNumber.valueChanges
      .pipe(debounceTime(400), distinctUntilChanged(), takeUntilDestroyed())
      .subscribe(() => {
        if (this.viewMode() === 'table') this.applyFilters();
      });

    this.filtersForm.controls.customerName.valueChanges
      .pipe(debounceTime(400), distinctUntilChanged(), takeUntilDestroyed())
      .subscribe(() => {
        if (this.viewMode() === 'table') this.applyFilters();
      });

    merge(
      this.filtersForm.controls.collectionMethod.valueChanges,
      this.filtersForm.controls.dueDateRange.valueChanges,
      this.filtersForm.controls.bucket.valueChanges
    )
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        if (this.viewMode() === 'table') this.applyFilters();
      });
  }

  ngOnInit(): void {
    try {
      localStorage.removeItem('pento.collections.board.panels');
    } catch {
      /* ignore */
    }
    if (this.viewMode() === 'board') {
      this.initialLoadDone.set(true);
      this.loadBoards();
    } else {
      this.load();
    }
  }

  load(): void {
    if (this.viewMode() === 'board') {
      this.refreshBoard();
      return;
    }

    this.loading.set(true);
    this.error.set(null);

    const direction = this.sortOrder() === 1 ? 'asc' : 'desc';
    const pageable = {
      page: this.currentPage(),
      size: this.pageSize(),
      sort: [`${this.sortField()},${direction}`]
    };

    this.chargeService.searchCharges(this.buildSpecFromFilters(), pageable).subscribe({
      next: (response) => {
        this.charges.set(response.body ?? []);
        this.totalItems.set(response.page?.totalItems ?? response.body?.length ?? 0);
        this.loading.set(false);
        this.initialLoadDone.set(true);
      },
      error: (err) => {
        this.error.set(err?.error?.message || err?.message || 'Error al cargar la cobranza');
        this.charges.set([]);
        this.totalItems.set(0);
        this.loading.set(false);
        this.initialLoadDone.set(true);
      }
    });
  }

  setViewMode(mode: CollectionsViewMode): void {
    if (this.viewMode() === mode) return;
    this.viewMode.set(mode);
    this.error.set(null);
    if (mode === 'board') {
      this.initialLoadDone.set(true);
      this.loadBoards();
    } else {
      this.currentPage.set(0);
      this.load();
    }
  }

  applyFilters(): void {
    this.syncActiveFilters();
    this.currentPage.set(0);
    this.load();
  }

  exportCsv(): void {
    if (!this.canExportCharges || this.exporting()) return;
    this.exporting.set(true);
    this.chargeService.exportChargesCsv(this.buildSpecFromFilters(), ['dueDate,asc']).subscribe({
      next: ({ blob, filename }) => {
        this.triggerBrowserDownload(blob, filename);
        this.exporting.set(false);
        this.toastr.success('CSV de cobranza descargado');
      },
      error: (err: HttpErrorResponse) => {
        this.exporting.set(false);
        const fallback = 'No se pudo exportar el CSV de cobranza';
        if (err.error instanceof Blob) {
          err.error.text().then((text) => {
            try {
              const json = JSON.parse(text) as { message?: string };
              this.toastr.error(json?.message?.trim() || fallback);
            } catch {
              this.toastr.error(fallback);
            }
          });
          return;
        }
        this.toastr.error(httpErrorApiMessage(err, fallback));
        console.error('Error exporting charges CSV:', err);
      }
    });
  }

  private triggerBrowserDownload(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || 'cobranza.csv';
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  clearFilters(): void {
    this.filtersForm.reset(
      {
        saleNumber: '',
        customerName: '',
        collectionMethod: null,
        dueDateRange: null,
        bucket: 'ALL'
      },
      { emitEvent: false }
    );
    this.applyFilters();
  }

  onLazyLoad(event: TableLazyLoadEvent): void {
    if (this.viewMode() !== 'table' || !this.initialLoadDone()) return;
    const size = event.rows ?? this.pageSize();
    const rawSortField = Array.isArray(event.sortField) ? event.sortField[0] : event.sortField;
    const nextSortField = rawSortField || this.sortField();
    const nextSortOrder = event.sortOrder ?? this.sortOrder();
    const sortChanged =
      nextSortField !== this.sortField() || nextSortOrder !== this.sortOrder();
    const page = sortChanged
      ? 0
      : size > 0
        ? Math.floor((event.first ?? 0) / size)
        : 0;

    const pageChanged = page !== this.currentPage() || size !== this.pageSize();
    this.sortField.set(nextSortField);
    this.sortOrder.set(nextSortOrder);
    this.currentPage.set(page);
    this.pageSize.set(size);
    if (sortChanged || pageChanged) this.load();
  }

  onPanelCustomerSearch(event: { query: string }): void {
    const q = (event.query ?? '').trim();
    if (q.length > 0 && q.length < 3) {
      this.panelCustomerOptions.set(this.normalizeCustomerOptions(this.panelForm.controls.customerSelection.value));
      return;
    }
    this.panelCustomerLoading.set(true);
    const spec = q.length >= 3 ? { 'legalName[lk]': q, 'comercialName[lk]': q } : {};
    this.customerService
      .listCustomers(spec, { page: 0, size: 30 })
      .pipe(
        catchError(() => of({ ok: false, body: [] as CustomerDTO[] })),
        finalize(() => this.panelCustomerLoading.set(false))
      )
      .subscribe((r) => {
        const found =
          r.ok && r.body
            ? r.body
                .map((c) => this.toCustomerOption(c))
                .filter((c): c is CustomerOption => c != null)
            : [];
        this.panelCustomerOptions.set(this.mergeCustomerOptions(found, this.panelForm.controls.customerSelection.value));
      });
  }

  getCustomerDisplayLabel(customer: Pick<CustomerDTO, 'comercialName' | 'legalName'> | null | undefined): string {
    const parts = [customer?.comercialName, customer?.legalName].filter(Boolean);
    return parts.join(' — ') || '-';
  }

  /** PrimeNG Autocomplete usa `option.value` como valor del form si existe; armamos objetos sin ese campo. */
  private toCustomerOption(
    c: (Partial<CustomerDTO> & { displayLabel?: string; label?: string }) | null | undefined
  ): CustomerOption | null {
    if (c?.id == null) return null;
    return {
      id: c.id,
      comercialName: c.comercialName,
      legalName: c.legalName,
      displayLabel:
        c.displayLabel ||
        c.label ||
        this.getCustomerDisplayLabel(c) ||
        `Cliente #${c.id}`
    };
  }

  private normalizeCustomerOptions(raw: unknown): CustomerOption[] {
    if (raw == null) return [];
    const list = Array.isArray(raw) ? raw : [raw];
    const byId = new Map<number, CustomerOption>();
    for (const item of list) {
      const opt = this.toCustomerOption(item as Partial<CustomerDTO> & { displayLabel?: string; label?: string });
      if (opt) byId.set(opt.id, opt);
    }
    return [...byId.values()];
  }

  private mergeCustomerOptions(found: CustomerOption[], selectedRaw: unknown): CustomerOption[] {
    const selected = this.normalizeCustomerOptions(selectedRaw);
    const byId = new Map<number, CustomerOption>();
    for (const c of found) byId.set(c.id, c);
    for (const c of selected) {
      if (!byId.has(c.id)) byId.set(c.id, c);
    }
    return [...byId.values()];
  }

  openCreatePanelDialog(): void {
    this.editingPanelId.set(null);
    this.resetPanelForm();
    this.panelDialogVisible.set(true);
  }

  openEditPanelDialog(panel: CollectionsBoardPanel): void {
    this.editingPanelId.set(panel.id);
    const f = panel.filters;
    const customers = this.normalizeCustomerOptions(f.customers ?? []);
    this.panelCustomerOptions.set(customers);
    this.panelForm.reset(
      {
        title: panel.title,
        scope: panel.scope ?? 'PERSONAL',
        customerSelection: customers,
        collectionMethods: f.collectionMethods?.length ? [...f.collectionMethods] : [],
        statuses: f.statuses?.length ? [...f.statuses] : [...this.pendingStatuses],
        overdueDaysFrom: f.overdueDaysFrom ?? null,
        overdueDaysTo: f.overdueDaysTo ?? null,
        dueInDaysFrom: f.dueInDaysFrom ?? null,
        dueInDaysTo: f.dueInDaysTo ?? null,
        attemptCountMin: f.attemptCountMin ?? null,
        cxcEmail: f.cxcEmail ?? null,
        cxcEmailOpened: f.cxcEmailOpened ?? null,
        cxcInternal: f.cxcInternal ?? null,
        lastSentEmailDaysFrom: f.lastSentEmailDaysFrom ?? null,
        lastSentEmailDaysTo: f.lastSentEmailDaysTo ?? null,
        lastSentInternalDaysFrom: f.lastSentInternalDaysFrom ?? null,
        lastSentInternalDaysTo: f.lastSentInternalDaysTo ?? null,
        saleFrequencies: f.saleFrequencies?.length ? [...f.saleFrequencies] : [],
        saleStatuses: f.saleStatuses?.length ? [...f.saleStatuses] : [],
        saleTypes: f.saleTypes?.length ? [...f.saleTypes] : []
      },
      { emitEvent: false }
    );
    this.panelDialogVisible.set(true);
  }

  onPanelDialogVisibleChange(visible: boolean): void {
    this.panelDialogVisible.set(visible);
    if (!visible) {
      this.editingPanelId.set(null);
      this.panelSaving.set(false);
    }
  }

  savePanel(): void {
    const raw = this.panelForm.getRawValue();
    const title = (raw.title || '').trim();
    if (!title) {
      this.toastr.warning('El panel necesita un nombre');
      return;
    }

    const overdueFrom = this.normalizeDays(raw.overdueDaysFrom);
    const overdueTo = this.normalizeDays(raw.overdueDaysTo);
    const dueInFrom = this.normalizeDays(raw.dueInDaysFrom);
    const dueInTo = this.normalizeDays(raw.dueInDaysTo);
    const lastSentEmailFrom = this.normalizeDays(raw.lastSentEmailDaysFrom);
    const lastSentEmailTo = this.normalizeDays(raw.lastSentEmailDaysTo);
    const lastSentInternalFrom = this.normalizeDays(raw.lastSentInternalDaysFrom);
    const lastSentInternalTo = this.normalizeDays(raw.lastSentInternalDaysTo);
    if (overdueFrom != null && overdueTo != null && overdueFrom > overdueTo) {
      this.toastr.warning('En “Vencido entre”, el mínimo no puede ser mayor que el máximo');
      return;
    }
    if (dueInFrom != null && dueInTo != null && dueInFrom > dueInTo) {
      this.toastr.warning('En “Vence entre”, el mínimo no puede ser mayor que el máximo');
      return;
    }
    if ((overdueFrom != null || overdueTo != null) && (dueInFrom != null || dueInTo != null)) {
      this.toastr.warning('Usa solo “Vencido entre” o “Vence entre”, no ambos a la vez');
      return;
    }
    if (lastSentEmailFrom != null && lastSentEmailTo != null && lastSentEmailFrom > lastSentEmailTo) {
      this.toastr.warning('En “Últ. correo”, el mínimo no puede ser mayor que el máximo');
      return;
    }
    if (lastSentInternalFrom != null && lastSentInternalTo != null && lastSentInternalFrom > lastSentInternalTo) {
      this.toastr.warning('En “Últ. nota”, el mínimo no puede ser mayor que el máximo');
      return;
    }

    const selectedCustomers = this.normalizeCustomerOptions(raw.customerSelection);
    const filters: CollectionsBoardPanelFilters = {
      customers: selectedCustomers.map((c) => ({
        id: c.id,
        label: c.displayLabel
      })),
      collectionMethods: raw.collectionMethods?.length ? [...raw.collectionMethods] : [],
      statuses: raw.statuses?.length ? [...raw.statuses] : [...this.pendingStatuses],
      overdueDaysFrom: overdueFrom,
      overdueDaysTo: overdueTo,
      dueInDaysFrom: dueInFrom,
      dueInDaysTo: dueInTo,
      attemptCountMin: this.normalizeDays(raw.attemptCountMin),
      cxcEmail: raw.cxcEmail ?? null,
      cxcEmailOpened: raw.cxcEmailOpened ?? null,
      cxcInternal: raw.cxcInternal ?? null,
      lastSentEmailDaysFrom: lastSentEmailFrom,
      lastSentEmailDaysTo: lastSentEmailTo,
      lastSentInternalDaysFrom: lastSentInternalFrom,
      lastSentInternalDaysTo: lastSentInternalTo,
      saleFrequencies: raw.saleFrequencies?.length ? [...raw.saleFrequencies] : [],
      saleStatuses: raw.saleStatuses?.length ? [...raw.saleStatuses] : [],
      saleTypes: raw.saleTypes?.length ? [...raw.saleTypes] : []
    };
    const scope = raw.scope ?? 'PERSONAL';
    const config = this.filtersToConfig(filters);
    const editingId = this.editingPanelId();

    this.panelSaving.set(true);
    const request$ = editingId
      ? this.boardService.update(editingId, {
          title,
          type: 'COLLECTIONS',
          scope,
          config
        })
      : this.boardService.create({
          title,
          type: 'COLLECTIONS',
          scope,
          sortOrder: this.boardPanels().length,
          config
        });

    request$.subscribe({
      next: (response) => {
        const saved = this.mapBoardDto(response.body);
        if (!saved) {
          this.panelSaving.set(false);
          this.toastr.error('Respuesta inválida al guardar el panel');
          return;
        }
        if (editingId) {
          this.boardPanels.update((panels) => panels.map((p) => (p.id === editingId ? saved : p)));
          this.toastr.success('Panel actualizado');
        } else {
          this.boardPanels.update((panels) => [...panels, saved]);
          this.toastr.success('Panel guardado');
        }
        this.panelDialogVisible.set(false);
        this.panelSaving.set(false);
        this.loadPanelResult(saved);
      },
      error: (err) => {
        this.panelSaving.set(false);
        this.toastr.error(err?.error?.message || err?.message || 'No se pudo guardar el panel');
      }
    });
  }

  deletePanel(panel: CollectionsBoardPanel, event?: Event): void {
    event?.stopPropagation();
    if (!confirm(`¿Eliminar el panel "${panel.title}"?`)) return;
    this.boardService.delete(panel.id).subscribe({
      next: () => {
        this.boardPanels.update((panels) => panels.filter((p) => p.id !== panel.id));
        this.boardResults.update((results) => {
          const next = { ...results };
          delete next[String(panel.id)];
          return next;
        });
        this.toastr.success('Panel eliminado');
      },
      error: (err) => {
        this.toastr.error(err?.error?.message || err?.message || 'No se pudo eliminar el panel');
      }
    });
  }

  loadBoards(): void {
    this.boardsLoading.set(true);
    this.boardService.list('COLLECTIONS').subscribe({
      next: (response) => {
        const panels = (response.body ?? [])
          .map((dto) => this.mapBoardDto(dto))
          .filter((p): p is CollectionsBoardPanel => p != null);
        this.boardPanels.set(panels);
        this.boardsLoading.set(false);
        this.refreshBoard();
      },
      error: (err) => {
        this.boardsLoading.set(false);
        this.boardPanels.set([]);
        this.toastr.error(err?.error?.message || err?.message || 'No se pudieron cargar los paneles');
      }
    });
  }

  refreshBoard(): void {
    for (const panel of this.boardPanels()) {
      this.loadPanelResult(panel);
    }
  }

  panelResult(panelId: number | string): BoardPanelResult {
    return (
      this.boardResults()[String(panelId)] ?? {
        loading: false,
        error: null,
        totalItems: 0
      }
    );
  }

  openPanelChargesDrawer(panel: CollectionsBoardPanel): void {
    this.selectedBoardPanel.set(panel);
    this.isPanelChargesDrawerOpen.set(true);
    this.panelDrawerPage.set(0);
    this.panelDrawerError.set(null);
    this.loadPanelDrawerCharges();
  }

  onPanelChargesDrawerVisibleChange(visible: boolean): void {
    this.isPanelChargesDrawerOpen.set(visible);
    if (!visible) {
      this.selectedBoardPanel.set(null);
      this.panelDrawerCharges.set([]);
      this.panelDrawerError.set(null);
      this.panelDrawerTotalItems.set(0);
      this.panelDrawerPage.set(0);
    }
  }

  onPanelDrawerLazyLoad(event: TableLazyLoadEvent): void {
    if (!this.isPanelChargesDrawerOpen() || !this.selectedBoardPanel()) return;
    const rows = event.rows ?? this.panelDrawerPageSize();
    const first = event.first ?? 0;
    const page = rows > 0 ? Math.floor(first / rows) : 0;
    const pageChanged = page !== this.panelDrawerPage() || rows !== this.panelDrawerPageSize();
    this.panelDrawerPage.set(page);
    this.panelDrawerPageSize.set(rows);
    if (pageChanged) this.loadPanelDrawerCharges();
  }

  private loadPanelDrawerCharges(): void {
    const panel = this.selectedBoardPanel();
    if (!panel) return;

    this.panelDrawerLoading.set(true);
    this.panelDrawerError.set(null);

    this.chargeService
      .searchCharges(this.buildSpecFromPanel(panel), {
        page: this.panelDrawerPage(),
        size: this.panelDrawerPageSize(),
        sort: ['dueDate,asc']
      })
      .subscribe({
        next: (response) => {
          const charges = response.body ?? [];
          this.panelDrawerCharges.set(charges);
          this.panelDrawerTotalItems.set(response.page?.totalItems ?? charges.length);
          this.panelDrawerLoading.set(false);
        },
        error: (err) => {
          this.panelDrawerCharges.set([]);
          this.panelDrawerTotalItems.set(0);
          this.panelDrawerError.set(
            err?.error?.message || err?.message || 'Error al cargar los recibos del panel'
          );
          this.panelDrawerLoading.set(false);
        }
      });
  }

  panelFilterChips(panel: CollectionsBoardPanel): string[] {
    const f = panel.filters;
    const chips: string[] = [];
    if (f.customers?.length) {
      if (f.customers.length === 1) {
        chips.push(f.customers[0].label);
      } else {
        chips.push(`${f.customers.length} clientes`);
      }
    }
    if (f.collectionMethods?.length) {
      const labels = f.collectionMethods.map((m) => this.methodLabel(m)).join(', ');
      chips.push(labels);
    }
    if (f.statuses?.length) {
      const labels = f.statuses
        .map((s) => this.statusFilterOptions.find((o) => o.value === s)?.label ?? s)
        .join(', ');
      chips.push(labels);
    }
    const overdueChip = this.formatRelativeDaysChip('Vencido', f.overdueDaysFrom, f.overdueDaysTo);
    if (overdueChip) chips.push(overdueChip);
    const dueInChip = this.formatRelativeDaysChip('Vence', f.dueInDaysFrom, f.dueInDaysTo);
    if (dueInChip) chips.push(dueInChip);
    if (f.attemptCountMin != null) chips.push(`Intentos ≥ ${f.attemptCountMin}`);
    if (f.cxcEmail != null) chips.push(f.cxcEmail ? 'Correo enviado' : 'Sin correo');
    if (f.cxcEmailOpened != null) chips.push(f.cxcEmailOpened ? 'Correo leído' : 'Correo no leído');
    if (f.cxcInternal != null) chips.push(f.cxcInternal ? 'Con nota interna' : 'Sin nota interna');
    const lastSentEmailChip = this.formatRelativeDaysChip(
      'Últ. correo',
      f.lastSentEmailDaysFrom,
      f.lastSentEmailDaysTo
    );
    if (lastSentEmailChip) chips.push(lastSentEmailChip);
    const lastSentInternalChip = this.formatRelativeDaysChip(
      'Últ. nota',
      f.lastSentInternalDaysFrom,
      f.lastSentInternalDaysTo
    );
    if (lastSentInternalChip) chips.push(lastSentInternalChip);
    if (f.saleFrequencies?.length) {
      chips.push(
        f.saleFrequencies
          .map((v) => this.saleFrequencyOptions.find((o) => o.value === v)?.label ?? v)
          .join(', ')
      );
    }
    if (f.saleStatuses?.length) {
      chips.push(
        f.saleStatuses.map((v) => this.saleStatusOptions.find((o) => o.value === v)?.label ?? v).join(', ')
      );
    }
    if (f.saleTypes?.length) {
      chips.push(
        f.saleTypes.map((v) => this.saleTypeOptions.find((o) => o.value === v)?.label ?? v).join(', ')
      );
    }
    return chips;
  }

  statusLabel(status: string | undefined): string {
    if (!status) return '—';
    return this.statusFilterOptions.find((o) => o.value === status)?.label ?? status;
  }

  loadPanelResult(panel: CollectionsBoardPanel): void {
    const key = String(panel.id);
    this.boardResults.update((results) => ({
      ...results,
      [key]: {
        loading: true,
        error: null,
        totalItems: results[key]?.totalItems ?? 0
      }
    }));

    this.boardService.summarizeCharges(this.buildSpecFromPanel(panel)).subscribe({
      next: (response) => {
        const summary = response.body;
        this.boardResults.update((results) => ({
          ...results,
          [key]: {
            loading: false,
            error: null,
            totalItems: summary?.count ?? 0
          }
        }));
      },
      error: (err) => {
        this.boardResults.update((results) => ({
          ...results,
          [key]: {
            loading: false,
            error: err?.error?.message || err?.message || 'Error al consultar el panel',
            totalItems: 0
          }
        }));
      }
    });
  }

  private resetPanelForm(): void {
    this.panelForm.reset(
      {
        title: '',
        scope: 'PERSONAL',
        customerSelection: [],
        collectionMethods: [],
        statuses: [...this.pendingStatuses],
        overdueDaysFrom: null,
        overdueDaysTo: null,
        dueInDaysFrom: null,
        dueInDaysTo: null,
        attemptCountMin: null,
        cxcEmail: null,
        cxcEmailOpened: null,
        cxcInternal: null,
        lastSentEmailDaysFrom: null,
        lastSentEmailDaysTo: null,
        lastSentInternalDaysFrom: null,
        lastSentInternalDaysTo: null,
        saleFrequencies: [],
        saleStatuses: [],
        saleTypes: []
      },
      { emitEvent: false }
    );
    this.panelCustomerOptions.set([]);
  }

  private syncActiveFilters(): void {
    const raw = this.filtersForm.getRawValue();
    const range = raw.dueDateRange;
    this.hasActiveFilters.set(
      !!(raw.saleNumber || '').trim() ||
        !!(raw.customerName || '').trim() ||
        !!raw.collectionMethod ||
        !!(range?.[0] && range?.[1]) ||
        (raw.bucket != null && raw.bucket !== 'ALL')
    );
  }

  private buildSpecFromFilters(): SearchChargeFilterSpec {
    const raw = this.filtersForm.getRawValue();
    const spec: SearchChargeFilterSpec = {
      'status[in]': [...this.pendingStatuses]
    };

    if ((raw.saleNumber || '').trim()) {
      spec['saleNumber[lk]'] = raw.saleNumber!.trim();
    }
    if ((raw.customerName || '').trim()) {
      spec['customerName[lk]'] = raw.customerName!.trim();
    }
    if (raw.collectionMethod) {
      spec['collectionMethod'] = raw.collectionMethod;
    }

    const range = raw.dueDateRange;
    if (range?.[0] && range?.[1]) {
      spec['dueDate[bw]'] = [this.toLocalDateStartIso(range[0]), this.toLocalDateEndIso(range[1])];
    }

    this.applyBucketToSpec(spec, raw.bucket ?? 'ALL');
    return spec;
  }

  private buildSpecFromPanel(panel: CollectionsBoardPanel): SearchChargeFilterSpec {
    const f = panel.filters;
    const spec: SearchChargeFilterSpec = {};
    const today = this.startOfToday();

    if (f.statuses?.length) {
      spec['status[in]'] = [...f.statuses];
    }
    if (f.customers?.length === 1) {
      spec['customerId'] = f.customers[0].id;
    } else if (f.customers?.length > 1) {
      spec['customerId[in]'] = f.customers.map((c) => c.id);
    }
    if (f.collectionMethods?.length === 1) {
      spec['collectionMethod'] = f.collectionMethods[0];
    } else if (f.collectionMethods?.length > 1) {
      spec['collectionMethod[in]'] = [...f.collectionMethods];
    }
    if (f.attemptCountMin != null) {
      spec['attemptCount[gte]'] = f.attemptCountMin;
    }
    if (f.cxcEmail != null) {
      spec['cxcEmail'] = f.cxcEmail;
    }
    if (f.cxcEmailOpened != null) {
      spec['cxcEmailOpened'] = f.cxcEmailOpened;
    }
    if (f.cxcInternal != null) {
      spec['cxcInternal'] = f.cxcInternal;
    }

    this.applyRelativeOverdueToSpec(spec, today, f.overdueDaysFrom, f.overdueDaysTo);
    this.applyRelativeDueInToSpec(spec, today, f.dueInDaysFrom, f.dueInDaysTo);
    this.applyLastSentDaysToSpec(
      spec,
      today,
      f.lastSentEmailDaysFrom,
      f.lastSentEmailDaysTo,
      'cxcLastSentEmail'
    );
    this.applyLastSentDaysToSpec(
      spec,
      today,
      f.lastSentInternalDaysFrom,
      f.lastSentInternalDaysTo,
      'cxcLastSentInternal'
    );

    if (f.saleFrequencies?.length === 1) {
      spec['saleFrequency'] = f.saleFrequencies[0];
    } else if (f.saleFrequencies?.length > 1) {
      spec['saleFrequency[in]'] = [...f.saleFrequencies];
    }
    if (f.saleStatuses?.length === 1) {
      spec['saleStatus'] = f.saleStatuses[0];
    } else if (f.saleStatuses?.length > 1) {
      spec['saleStatus[in]'] = [...f.saleStatuses];
    }
    if (f.saleTypes?.length === 1) {
      spec['saleType'] = f.saleTypes[0];
    } else if (f.saleTypes?.length > 1) {
      spec['saleType[in]'] = [...f.saleTypes];
    }

    return spec;
  }

  /**
   * Vencido entre N y M días.
   * Solo from → atraso ≥ from
   * Solo to → atraso ≤ to
   * Ambos → between
   */
  private applyRelativeOverdueToSpec(
    spec: SearchChargeFilterSpec,
    today: Date,
    from: number | null | undefined,
    to: number | null | undefined
  ): void {
    if (from == null && to == null) return;
    // Siempre debe estar vencido (antes de hoy), salvo que from/to lo acoten.
    if (from != null && from > 0) {
      spec['dueDate[lte]'] = this.toLocalDateEndIso(this.addDays(today, -from));
    } else {
      spec['dueDate[lt]'] = this.toLocalDateStartIso(today);
    }
    if (to != null) {
      spec['dueDate[gte]'] = this.toLocalDateStartIso(this.addDays(today, -to));
    }
  }

  /**
   * Vence entre N y M días.
   * Solo from → vence en ≥ from días
   * Solo to → vence en ≤ to días
   * Ambos → between
   */
  private applyRelativeDueInToSpec(
    spec: SearchChargeFilterSpec,
    today: Date,
    from: number | null | undefined,
    to: number | null | undefined
  ): void {
    if (from == null && to == null) return;
    const lower = from != null ? this.addDays(today, from) : today;
    spec['dueDate[gte]'] = this.toLocalDateStartIso(lower);
    if (to != null) {
      spec['dueDate[lte]'] = this.toLocalDateEndIso(this.addDays(today, to));
    }
  }

  /**
   * Días desde el último envío por canal (email o internal).
   * Solo from → sin actividad desde hace ≥ from días
   * Solo to → última actividad hace ≤ to días
   * Ambos → entre from y to días
   */
  private applyLastSentDaysToSpec(
    spec: SearchChargeFilterSpec,
    today: Date,
    from: number | null | undefined,
    to: number | null | undefined,
    paramPrefix: 'cxcLastSentEmail' | 'cxcLastSentInternal'
  ): void {
    if (from == null && to == null) return;
    if (from != null) {
      spec[`${paramPrefix}[lte]`] = this.toLocalDateEndIso(this.addDays(today, -from));
    }
    if (to != null) {
      spec[`${paramPrefix}[gte]`] = this.toLocalDateStartIso(this.addDays(today, -to));
    }
  }

  private applyBucketToSpec(spec: SearchChargeFilterSpec, bucket: CollectionBucketFilter): void {
    const today = this.startOfToday();
    if (bucket === 'OVERDUE') {
      spec['dueDate[lt]'] = this.toLocalDateStartIso(today);
    } else if (bucket === 'UPCOMING') {
      spec['dueDate[gte]'] = this.toLocalDateStartIso(today);
    }
  }

  private normalizeDays(value: number | null | undefined): number | null {
    if (value == null || Number.isNaN(value)) return null;
    const n = Math.floor(Number(value));
    return n < 0 ? null : n;
  }

  private formatRelativeDaysChip(
    label: string,
    from: number | null | undefined,
    to: number | null | undefined
  ): string | null {
    if (from == null && to == null) return null;
    if (from != null && to != null) {
      if (from === to) return `${label} entre ${from} días`;
      return `${label} entre ${from} y ${to} días`;
    }
    if (from != null) return `${label} ≥ ${from} días`;
    return `${label} ≤ ${to} días`;
  }

  private startOfToday(): Date {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  }

  private addDays(date: Date, days: number): Date {
    const d = new Date(date);
    d.setDate(d.getDate() + days);
    return d;
  }

  private toLocalDateStartIso(date: Date): string {
    return `${this.toLocalDatePart(date)}T00:00:00.000Z`;
  }

  private toLocalDateEndIso(date: Date): string {
    return `${this.toLocalDatePart(date)}T23:59:59.999Z`;
  }

  private toLocalDatePart(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  private mapBoardDto(dto: {
    id?: number;
    title?: string;
    scope?: BoardScope;
    sortOrder?: number;
    config?: BoardConfigDocument;
  } | null | undefined): CollectionsBoardPanel | null {
    if (dto?.id == null || !dto.title) return null;
    return {
      id: dto.id,
      title: dto.title,
      scope: dto.scope === 'GLOBAL' ? 'GLOBAL' : 'PERSONAL',
      sortOrder: dto.sortOrder ?? 0,
      filters: this.configToFilters(dto.config)
    };
  }

  private filtersToConfig(filters: CollectionsBoardPanelFilters): BoardConfigDocument {
    return {
      version: 1,
      customers: filters.customers.map((c) => ({ id: c.id, label: c.label })),
      collectionMethods: [...filters.collectionMethods],
      statuses: [...filters.statuses],
      overdueDaysFrom: filters.overdueDaysFrom,
      overdueDaysTo: filters.overdueDaysTo,
      dueInDaysFrom: filters.dueInDaysFrom,
      dueInDaysTo: filters.dueInDaysTo,
      attemptCountMin: filters.attemptCountMin,
      cxcEmail: filters.cxcEmail,
      cxcEmailOpened: filters.cxcEmailOpened,
      cxcInternal: filters.cxcInternal,
      lastSentEmailDaysFrom: filters.lastSentEmailDaysFrom,
      lastSentEmailDaysTo: filters.lastSentEmailDaysTo,
      lastSentInternalDaysFrom: filters.lastSentInternalDaysFrom,
      lastSentInternalDaysTo: filters.lastSentInternalDaysTo,
      saleFrequencies: [...filters.saleFrequencies],
      saleStatuses: [...filters.saleStatuses],
      saleTypes: [...filters.saleTypes]
    };
  }

  private configToFilters(config: BoardConfigDocument | null | undefined): CollectionsBoardPanelFilters {
    const c = config ?? {};
    const customers = Array.isArray(c.customers)
      ? c.customers
          .filter((x) => x?.id != null)
          .map((x) => ({ id: Number(x.id), label: x.label || `Cliente #${x.id}` }))
      : [];
    return {
      customers,
      collectionMethods: Array.isArray(c.collectionMethods)
        ? (c.collectionMethods as CollectionMethodEnumValue[])
        : [],
      statuses: Array.isArray(c.statuses) && c.statuses.length
        ? (c.statuses as ChargeOperationalStatusEnumValue[])
        : [...this.pendingStatuses],
      overdueDaysFrom: c.overdueDaysFrom ?? null,
      overdueDaysTo: c.overdueDaysTo ?? null,
      dueInDaysFrom: c.dueInDaysFrom ?? null,
      dueInDaysTo: c.dueInDaysTo ?? null,
      attemptCountMin: c.attemptCountMin ?? null,
      cxcEmail: typeof c.cxcEmail === 'boolean' ? c.cxcEmail : null,
      cxcEmailOpened: typeof c.cxcEmailOpened === 'boolean' ? c.cxcEmailOpened : null,
      cxcInternal: typeof c.cxcInternal === 'boolean' ? c.cxcInternal : null,
      lastSentEmailDaysFrom: c.lastSentEmailDaysFrom ?? null,
      lastSentEmailDaysTo: c.lastSentEmailDaysTo ?? null,
      lastSentInternalDaysFrom: c.lastSentInternalDaysFrom ?? null,
      lastSentInternalDaysTo: c.lastSentInternalDaysTo ?? null,
      saleFrequencies: Array.isArray(c.saleFrequencies)
        ? (c.saleFrequencies as FrequencyEnumValue[])
        : [],
      saleStatuses: Array.isArray(c.saleStatuses) ? [...c.saleStatuses] : [],
      saleTypes: Array.isArray(c.saleTypes) ? (c.saleTypes as SaleTypeEnumValue[]) : []
    };
  }

  saleNumberLabel(charge: ChargeDTO): string {
    const number = charge.sale?.number?.trim();
    if (number) return number;
    if (charge.saleId != null) return `#${charge.saleId}`;
    return '—';
  }

  customerName(charge: ChargeDTO): string {
    const customer = charge.sale?.customer;
    return customer?.comercialName || customer?.legalName || (charge.customerId != null ? `Cliente #${charge.customerId}` : '—');
  }

  methodLabel(method: string | undefined): string {
    if (!method) return '—';
    return this.collectionMethodOptions.find((o) => o.value === method)?.label ?? method;
  }

  daysUntilDue(dueDate: string | undefined): number | null {
    if (!dueDate) return null;
    const match = String(dueDate).trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!match) return null;
    const due = new Date(parseInt(match[1], 10), parseInt(match[2], 10) - 1, parseInt(match[3], 10));
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    due.setHours(0, 0, 0, 0);
    return Math.round((due.getTime() - today.getTime()) / 86400000);
  }

  daysLabel(days: number): string {
    return `${Math.abs(days)} días atrasado`;
  }

  /**
   * Un solo badge CxC. Prioridad (menor → mayor):
   * correo enviado, correo leído, bitácora, compromiso de pago,
   * compromiso vencido (recibo vencido + fecha compromiso pasada).
   */
  cxcStatusBadge(charge: ChargeDTO): { icon: string; badgeClass: string; tooltip: string } | null {
    const cxc = charge.cxc;
    if (!cxc) return null;

    if (cxc.commitmentDate) {
      const commitmentLabel = formatApiDateOnly(cxc.commitmentDate) || this.formatCxcDateTime(cxc.commitmentDate);
      if (this.isBrokenPaymentCommitment(charge)) {
        return {
          icon: 'pi pi-exclamation-triangle',
          badgeClass: 'bg-rose-600',
          tooltip: `Compromiso vencido · ${commitmentLabel}`
        };
      }
      return {
        icon: 'pi pi-calendar',
        badgeClass: 'bg-violet-500',
        tooltip: `Compromiso de pago · ${commitmentLabel}`
      };
    }
    if (cxc.internal) {
      const at = cxc.lastSentInternal;
      return {
        icon: 'pi pi-book',
        badgeClass: 'bg-amber-500',
        tooltip: at ? `Bitácora · ${this.formatCxcDateTime(at)}` : 'Bitácora'
      };
    }
    if (cxc.emailOpened) {
      const at = cxc.emailOpenedAt;
      return {
        icon: 'pi pi-eye',
        badgeClass: 'bg-emerald-500',
        tooltip: at ? `Correo abierto · ${this.formatCxcDateTime(at)}` : 'Correo abierto'
      };
    }
    if (cxc.email) {
      const at = cxc.lastSentEmail;
      return {
        icon: 'pi pi-send',
        badgeClass: 'bg-sky-500',
        tooltip: at ? `Correo enviado · ${this.formatCxcDateTime(at)}` : 'Correo enviado'
      };
    }
    return null;
  }

  cxcEmailCheckTooltip(charge: ChargeDTO): string {
    const cxc = charge.cxc;
    if (!cxc?.email) return 'Sin correo';
    const parts = ['Correo enviado'];
    if (cxc.lastSentEmail) parts.push(this.formatCxcDateTime(cxc.lastSentEmail));
    if (cxc.emailOpened) {
      parts.push(cxc.emailOpenedAt ? `Abierto · ${this.formatCxcDateTime(cxc.emailOpenedAt)}` : 'Abierto');
    }
    return parts.join(' · ');
  }

  cxcInternalCheckTooltip(charge: ChargeDTO): string {
    const at = charge.cxc?.lastSentInternal;
    return at ? `Bitácora · ${this.formatCxcDateTime(at)}` : 'Bitácora';
  }

  /** Recibo vencido, con fecha compromiso y esa fecha ya pasó. */
  isBrokenPaymentCommitment(charge: ChargeDTO): boolean {
    const commitmentDate = charge.cxc?.commitmentDate;
    if (!commitmentDate) return false;
    if (
      charge.status === ChargeOperationalStatusEnum.PAID ||
      charge.status === ChargeOperationalStatusEnum.CANCELLED
    ) {
      return false;
    }
    const daysUntilDue = this.daysUntilDue(charge.dueDate);
    const daysUntilCommitment = calendarDaysUntil(commitmentDate);
    return daysUntilDue != null && daysUntilDue < 0
      && daysUntilCommitment != null && daysUntilCommitment < 0;
  }

  formatCommitmentDate(iso: string | null | undefined): string {
    if (!iso) return '';
    return formatApiDateOnly(iso) || this.formatCxcDateTime(iso);
  }

  private formatCxcDateTime(iso: string): string {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }

  openChargeDetails(charge: ChargeDTO): void {
    if (!charge.id || this.chargeDetailsLoading()) return;
    this.chargeDetailsLoading.set(true);
    this.chargeService.searchCharges({ id: charge.id }, { page: 0, size: 1 }).subscribe({
      next: (response) => {
        const found = response.body?.[0] ?? charge;
        this.chargeDetailsLoading.set(false);
        this.selectedCharge.set(found);
        this.isDetailsOpen.set(true);
      },
      error: () => {
        this.chargeDetailsLoading.set(false);
        this.selectedCharge.set(charge);
        this.isDetailsOpen.set(true);
      }
    });
  }

  onDrawerVisibleChange(visible: boolean): void {
    this.isDetailsOpen.set(visible);
    if (!visible) this.selectedCharge.set(null);
  }

  openSeguimientoDialog(event: Event, charge: ChargeDTO): void {
    event.stopPropagation();
    if (!charge.id) return;
    this.seguimientoDialogChargeId.set(charge.id);
    this.seguimientoDialogSaleId.set(charge.saleId ?? charge.sale?.id ?? null);
    this.seguimientoDialogSubtitle.set(
      [this.saleNumberLabel(charge), this.customerName(charge)].filter((v) => v && v !== '—').join(' · ') ||
        'Recibo de pago'
    );
    this.seguimientoDialogVisible.set(true);
  }

  onSeguimientoDialogVisibleChange(visible: boolean): void {
    this.seguimientoDialogVisible.set(visible);
    if (!visible) {
      this.seguimientoDialogChargeId.set(null);
      this.seguimientoDialogSaleId.set(null);
      this.seguimientoDialogSubtitle.set(null);
    }
  }

  openSaleDetails(saleId: number | undefined, event?: Event): void {
    event?.stopPropagation();
    if (!saleId) return;
    this.isSaleDetailsOpen.set(true);
    this.selectedSale.set(null);
    this.saleCharges.set([]);
    this.saleDetailsLoading.set(true);
    this.saleService.getSaleById(saleId).subscribe({
      next: (response) => {
        if (response.ok && response.body) {
          const detail = response.body;
          this.selectedSale.set(detail);
          this.saleService.getSaleCharges(detail.id!).subscribe({
            next: (chargesResp) => this.saleCharges.set(chargesResp.body ?? []),
            error: () => this.saleCharges.set([])
          });
        } else {
          this.toastr.error(response.message || 'No se pudo cargar el detalle de la venta');
          this.closeSaleDetails();
        }
        this.saleDetailsLoading.set(false);
      },
      error: () => {
        this.saleDetailsLoading.set(false);
        this.toastr.error('Error al cargar el detalle de la venta');
        this.closeSaleDetails();
      }
    });
  }

  closeSaleDetails(): void {
    this.isSaleDetailsOpen.set(false);
    this.selectedSale.set(null);
    this.saleCharges.set([]);
    this.saleDetailsLoading.set(false);
  }

  onSaleDrawerVisibleChange(visible: boolean): void {
    if (!visible) this.closeSaleDetails();
  }

  openSaleDetailsFromCustomer(saleId: number): void {
    this.openSaleDetails(saleId);
  }

  openCustomerDetails(customerId: number | undefined, event?: Event): void {
    event?.stopPropagation();
    if (!customerId) return;
    this.isCustomerDetailsOpen.set(true);
    this.selectedCustomer.set(null);
    this.customerDetailsLoading.set(true);
    this.customerService.getCustomerById(customerId).subscribe({
      next: (response) => {
        if (response.ok && response.body) {
          this.selectedCustomer.set(response.body);
        } else {
          this.toastr.error(response.message || 'No se pudo cargar el cliente');
          this.closeCustomerDetails();
        }
        this.customerDetailsLoading.set(false);
      },
      error: () => {
        this.customerDetailsLoading.set(false);
        this.toastr.error('Error al cargar el cliente');
        this.closeCustomerDetails();
      }
    });
  }

  closeCustomerDetails(): void {
    this.isCustomerDetailsOpen.set(false);
    this.selectedCustomer.set(null);
    this.customerDetailsLoading.set(false);
  }

  onCustomerDrawerVisibleChange(visible: boolean): void {
    if (!visible) this.closeCustomerDetails();
  }
}
